// ./src/utils/trackingGeoUtils.ts
//
// Client-side geospatial utility functions for live GPS tracking.
// Pure functions with no dependencies — used by the tracking filter,
// distance accumulator, and any component that needs geo calculations.
//
// These mirror the backend trackingGeoUtils.ts but are kept separate
// to avoid sharing code between client and server bundles.

// ============================================
// Constants
// ============================================

/** Earth's mean radius in meters (WGS-84) */
const EARTH_RADIUS_M = 6371008.8;

// ============================================
// Types
// ============================================

export interface GeoPoint {
  lat: number;
  lng: number;
}

// ============================================
// Core Functions
// ============================================

/** Convert degrees to radians */
const toRad = (deg: number): number => (deg * Math.PI) / 180;

/**
 * Calculate the great-circle distance between two points using the Haversine formula.
 *
 * @param p1 - First point (lat/lng in degrees)
 * @param p2 - Second point (lat/lng in degrees)
 * @returns Distance in meters
 */
export const haversineDistance = (p1: GeoPoint, p2: GeoPoint): number => {
  const dLat = toRad(p2.lat - p1.lat);
  const dLng = toRad(p2.lng - p1.lng);
  const lat1 = toRad(p1.lat);
  const lat2 = toRad(p2.lat);

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

  return EARTH_RADIUS_M * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

/**
 * Calculate the initial bearing (forward azimuth) from p1 to p2.
 *
 * @param p1 - Start point (lat/lng in degrees)
 * @param p2 - End point (lat/lng in degrees)
 * @returns Bearing in degrees (0–360, where 0 = North, 90 = East)
 */
export const calculateBearing = (p1: GeoPoint, p2: GeoPoint): number => {
  const lat1 = toRad(p1.lat);
  const lat2 = toRad(p2.lat);
  const dLng = toRad(p2.lng - p1.lng);

  const y = Math.sin(dLng) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLng);

  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

/**
 * Calculate the smallest angular difference between two bearings.
 * Handles the 0°/360° wraparound correctly.
 *
 * @param b1 - First bearing in degrees (0–360)
 * @param b2 - Second bearing in degrees (0–360)
 * @returns Absolute angular difference in degrees (0–180)
 *
 * @example
 * bearingDelta(10, 350) // → 20 (not 340)
 * bearingDelta(0, 180)  // → 180
 */
export const bearingDelta = (b1: number, b2: number): number => {
  const diff = Math.abs(b1 - b2) % 360;
  return diff > 180 ? 360 - diff : diff;
};

/**
 * Calculate the total distance of a path by summing consecutive
 * haversine distances between points.
 *
 * @param points - Array of points in order
 * @returns Total distance in meters
 */
export const calculateTotalDistance = (points: GeoPoint[]): number => {
  if (points.length < 2) return 0;

  let total = 0;
  for (let i = 1; i < points.length; i++) {
    total += haversineDistance(points[i - 1], points[i]);
  }
  return total;
};

// ============================================
// Unit Converters
// ============================================

/** Convert meters to kilometers */
export const metersToKm = (meters: number): number => meters / 1000;

/** Convert meters per second to km/h */
export const mpsToKmh = (mps: number): number => mps * 3.6;

/** Convert meters per second to mph */
export const mpsToMph = (mps: number): number => mps * 2.23694;

/** Convert km to miles */
export const kmToMiles = (km: number): number => km * 0.621371;